import React, { useMemo, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Card, CardBody, Input, Label } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useAttivitaData } from '../../../hook/useAttivitaData';
import { H3 } from '../../../AbstractElements';
import Loader from '../../../Components/Widgets/Loader/generic';
type Commento = {
    ID: number;
    attivitaID: number;
    userID: number;
    commento: string;
    cancellato: number;
    data_upd: string | null;
    data_ins?: string | null;
    nominativo?: string | null;
};
type Attivita = {
    ID: number;
    progettoID: number;
    descrizione: string;
    progettoNome: string;
    stato: number;
    tipo: number;
    rapportino: number;
    tempo_esecuzione: string | null;
    data_svolgere: string | null;
    data_esecuzione: string | null;
    creato_da: number;
    cancellato: number;
    data_ins: string | null;
    data_cancellazione: string | null;
    priorita: number;
    file_nome: string | null;
    file_url: string | null;
    commenti: Commento[] | null;
    task_commenti: Commento[] | null;
    nominativo?: string | null;
    progetto_nome?: string | null;
};
function fmtDate(iso?: string | null) {
    if (!iso) return '-';
    const s = iso.trim();
    if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return iso;
    const [y, m, d] = s.split(' ')[0].split('T')[0].split('-');
    return `${d}/${m}/${y}`;
}
function fmtDateTime(iso?: string | null) {
    if (!iso) return '-';
    const date = new Date(iso.replace(' ', 'T'));
    if (isNaN(date.getTime())) return iso;
    return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
function hasComments(row: Attivita) {
    return (row.commenti?.length ?? 0) > 0 || (row.task_commenti?.length ?? 0) > 0;
}
function CommentiAttivita({ data }: { data: Attivita }) {
    const commenti = data.commenti ?? [];
    const taskCommenti = data.task_commenti ?? [];
    if (commenti.length === 0 && taskCommenti.length === 0) {
        return null;
    }
    return (
        <div className="px-4 py-3 bg-light border-top">
            {commenti.length > 0 && (
                <div style={{color:'black'}} className={taskCommenti.length > 0 ? 'mb-3' : ''}>
                    <div className="fw-bold mb-2">
                        <i  className="fa-regular fa-comments me-2"></i>
                        Commenti
                    </div>
                    {commenti.map((commento) => (
                        <div
                            key={`commento-${commento.ID}`}
                            className="bg-white border rounded p-3 mb-2"
                        >
                            <div
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '1.5',
                                }}
                            >
                                {commento.commento}
                            </div>
                            <div className="text-muted small mt-2">
                                {commento.nominativo && (
                                    <span className="me-3">
                                        <i className="fa-regular fa-user me-1"></i>
                                        {commento.nominativo}
                                    </span>
                                )}
                                {commento.data_upd && (
                                    <span>
                                        <i className="fa-regular fa-clock me-1"></i>
                                        {fmtDateTime(commento.data_upd)}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {taskCommenti.length > 0 && (
                <div>
                    <div className="fw-bold mb-2">
                        <i className="fa-solid fa-list-check me-2"></i>
                        Commenti task
                    </div>
                    {taskCommenti.map((commento) => (
                        <div
                            key={`task-commento-${commento.ID}`}
                            className="bg-white border rounded p-3 mb-2"
                        >
                            <div
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '1.5',
                                }}
                            >
                                {commento.commento}
                            </div>
                            <div className="text-muted small mt-2">
                                {commento.nominativo && (
                                    <span className="me-3">
                                        <i className="fa-regular fa-user me-1"></i>
                                        {commento.nominativo}
                                    </span>
                                )}
                                {commento.data_upd && (
                                    <span>
                                        <i className="fa-regular fa-clock me-1"></i>
                                        {fmtDateTime(commento.data_upd)}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
export default function Report1() {
    const navigate = useNavigate();
    const [data, setData] = useState('');
    const [filterText, setFilterText] = useState('');
    const { rows, loading, error } = useAttivitaData<Attivita>(data);
    const columns: TableColumn<Attivita>[] = useMemo(
        () => [
            {
                name: 'Progetto',
                selector: (r) => r.progettoNome,
                sortable: true,
                width: '300px',
            },
            {
                name: 'Attività',
                selector: (r) => r.descrizione ?? '',
                sortable: true,
                grow: 3,
                cell: (r) => (
                    <div className="py-2">
                        <div>{r.descrizione}</div>
                        {hasComments(r) && (
                            <div className="small text-primary mt-1">
                                <i className="fa-regular fa-comments me-1"></i>
                                {(r.commenti?.length ?? 0) + (r.task_commenti?.length ?? 0)} commenti
                            </div>
                        )}
                    </div>
                ),
            },
            {
                name: 'Data da svolgere',
                selector: (r) => r.data_svolgere ?? '',
                sortable: true,
                width: '150px',
                cell: (r) => fmtDate(r.data_svolgere),
            },
            {
                name: 'Data esecuzione',
                selector: (r) => r.data_esecuzione ?? '',
                sortable: true,
                width: '150px',
                cell: (r) => fmtDate(r.data_esecuzione),
            },
            {
                name: 'Tempo',
                selector: (r) => r.tempo_esecuzione ?? '',
                sortable: true,
                width: '110px',
                center: true,
                cell: (r) => r.tempo_esecuzione ?? '-',
            },
            {
                name: 'Rapportino',
                selector: (r) => r.rapportino,
                sortable: true,
                width: '120px',
                center: true,
                cell: (r) => (r.rapportino === 1 ? '✅' : '❌'),
            },
            {
                name: 'Azioni',
                width: '100px',
                button: true,
                ignoreRowClick: true,
                cell: (r) => (
                    <button
                        type="button"
                        className="btn btn-sm btn-info"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/progetti/${r.progettoID}/${r.ID}`);
                        }}
                        title="Apri progetto"
                    >
                        <i className="fa-solid fa-arrow-up-right-from-square"></i>
                    </button>
                ),
            },
        ],
        [navigate]
    );
    const filteredRows = useMemo(() => {
        const term = filterText.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter((r) => {
            const descrizione = r.descrizione?.toLowerCase() ?? '';
            const progetto = r.progetto_nome?.toLowerCase() ?? '';
            const nominativo = r.nominativo?.toLowerCase() ?? '';
            const neiCommenti = (r.commenti ?? []).some((c) =>
                c.commento?.toLowerCase().includes(term)
            );
            const neiTaskCommenti = (r.task_commenti ?? []).some((c) =>
                c.commento?.toLowerCase().includes(term)
            );
            return descrizione.includes(term) ||
                progetto.includes(term) ||
                nominativo.includes(term) ||
                neiCommenti ||
                neiTaskCommenti;
        });
    }, [rows, filterText]);
    return (
        <div className="page-body pt-4">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <H3 className="mb-0">Report attività</H3>
                    </div>
                    <div className="mb-3 d-flex align-items-end gap-3">
                        <div>
                            <Label htmlFor="data-attivita">Data</Label>
                            <Input
                                id="data-attivita"
                                type="date"
                                value={data}
                                onChange={(e) => setData(e.target.value)}
                            />
                        </div>
                        {/* <div className="flex-grow-1">
                            <Label htmlFor="filtro-attivita">Cerca</Label>
                            <Input
                                id="filtro-attivita"
                                type="text"
                                placeholder="Filtra attività o commenti..."
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                        </div> */}
                    </div>
                    {error && (
                        <div className="alert alert-danger">
                            {error}
                        </div>
                    )}
                    <div className="table-responsive">
                        <DataTable
                            columns={columns}
                            data={filteredRows}
                            progressPending={loading}
                            progressComponent={<Loader />}
                            striped
                            pagination
                            highlightOnHover
                            dense
                            paginationPerPage={50}
                            expandableRows
                            expandableRowsComponent={CommentiAttivita}
                            expandableRowDisabled={(row) => !hasComments(row)}
                            expandableRowExpanded={(row) => hasComments(row)}
                            noDataComponent={
                                data
                                    ? 'Nessuna attività disponibile per la data selezionata'
                                    : 'Seleziona una data'
                            }
                            customStyles={{
                                rows: {
                                    style: {
                                        minHeight: '45px',
                                    },
                                },
                                expanderRow: {
                                    style: {
                                        backgroundColor: '#f8f9fa',
                                    },
                                },
                            }}
                        />
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}